import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PageHeader from "@/components/landing/PageHeader";
import SEO from "@/components/SEO";
import usePageTitle from "@/hooks/usePageTitle";

export default function AccessibilityPage() {
  usePageTitle("Accessibility Statement");
  return (
    <div className="bg-[var(--sci-bg)]">
      <SEO
        title="Accessibility Statement"
        description="International Sci Ink Press accessibility statement — WCAG 2.1 AA conformance, keyboard navigation, screen reader support, and reporting channels."
        path="/accessibility"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Accessibility", url: "/accessibility" },
        ]}
      />
      <Navbar />
      <PageHeader
        overline="Accessibility Statement"
        title={
          <>
            Designed for{" "}
            <em className="not-italic text-[var(--sci-teal)]">everyone</em>.
          </>
        }
        sub="International Sci Ink Press is committed to digital accessibility. This page documents our conformance with WCAG 2.1 Level AA and how to report issues."
      />
      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 prose-editorial">
          <Block title="Standards we follow">
            <p>
              This site targets <strong>Web Content Accessibility Guidelines (WCAG) 2.1 Level AA</strong> conformance. We also reference the EN 301 549 European accessibility standard.
            </p>
          </Block>
          <Block title="What we provide">
            <ul>
              <li>Semantic HTML with landmark roles (<code>header</code>, <code>nav</code>, <code>main</code>, <code>footer</code>).</li>
              <li>Keyboard-only navigation for all interactive elements including the Ctrl/Cmd + K command palette.</li>
              <li>Visible focus outlines that respect operating-system focus rings.</li>
              <li>Text contrast verified at AA threshold against the Sci Ink palette.</li>
              <li>ARIA labels on icon-only buttons.</li>
              <li>Reduced-motion respect — heavy animations dim or freeze when <code>prefers-reduced-motion</code> is set.</li>
              <li>Decorative SVGs marked <code>aria-hidden="true"</code> so they are skipped by assistive technology.</li>
              <li>Screen-reader-friendly skip target on every section (<code>id</code> + heading hierarchy).</li>
            </ul>
          </Block>
          <Block title="Known limitations">
            <ul>
              <li>The embedded Nutramilo iframe inherits its own accessibility profile, which we do not control. We provide an inline fallback summary and external link.</li>
              <li>Hand-drawn map labels in the Global Research Community section are presented in supporting text below the visualization.</li>
            </ul>
          </Block>
          <Block title="Report an accessibility issue">
            <p>
              Please report accessibility issues through the journal portals at{" "}
              <a
                href="https://www.mdpub.net"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="accessibility-mdpub"
                className="text-[var(--sci-teal)] underline"
              >
                mdpub.net
              </a>{" "}
              or{" "}
              <a
                href="https://www.ejos.org"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="accessibility-ejos"
                className="text-[var(--sci-teal)] underline"
              >
                ejos.org
              </a>
              . Include the URL you visited, your assistive technology, and a short description of the issue. We aim to acknowledge within two business days.
            </p>
          </Block>
          <Block title="Last reviewed">
            <p className="font-mono-sci text-sm tracking-wider text-[var(--sci-text-muted)]">
              JUNE 2026
            </p>
          </Block>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight mb-4">
        {title}
      </h2>
      <div className="text-[var(--sci-text)]/85 leading-relaxed space-y-3 [&_a]:text-[var(--sci-teal)] [&_a]:underline [&_code]:font-mono-sci [&_code]:text-xs [&_code]:bg-[var(--sci-bg)] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:border [&_code]:border-[var(--sci-border)] [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2">
        {children}
      </div>
    </section>
  );
}
